import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { forkJoin, of, catchError, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface NameplateItem {
  id: number;
  title: string;
  description: string;
  backgroundUrl: string;
  backgroundStyle: string;
  isDefault?: boolean;
}

export function formatNameplateBgStyle(bgUrl: string | undefined | null): string {
  if (!bgUrl) return 'linear-gradient(135deg, #1f1f1f 0%, #111111 100%)';
  if (bgUrl.includes('background-image:')) {
    const match = bgUrl.match(/url\(['"]?([^'"\)]+)['"]?\)/);
    if (match && match[1]) {
      return `url("${match[1]}")`;
    }
  }
  if (bgUrl.startsWith('url(') || bgUrl.startsWith('linear-gradient(')) {
    return bgUrl;
  }
  return `url("${bgUrl}")`;
}

@Injectable({
  providedIn: 'root',
})
export class NameplateService {
  private httpClient = inject(HttpClient);

  public nameplates = signal<NameplateItem[]>([]);
  public ownedNameplates = signal<any[]>([]);

  public defaultCatalog: NameplateItem[] = [
    {
      id: 11,
      title: 'Spades 10+ Wins Nameplate',
      description: 'Awarded for 10+ Spades victories. Features paint-splatter Spade artwork and gold 10+ emblem.',
      backgroundUrl: '/images/name-plates/spades-nameplate.png',
      backgroundStyle: 'background-image: url(/images/name-plates/spades-nameplate.png) !important;'
    },
    {
      id: 12,
      title: 'Spades 50+ Wins Nameplate',
      description: 'Awarded for 50+ Spades victories. Features cyan/gold energy particles and gold 50+ emblem.',
      backgroundUrl: '/images/name-plates/spades-50-nameplate.png',
      backgroundStyle: 'background-image: url(/images/name-plates/spades-50-nameplate.png) !important;'
    },
    {
      id: 13,
      title: 'Spades 100+ Wins Nameplate',
      description: 'Awarded for 100+ Spades victories. Features ruby-gold stardust aura and gold 100+ emblem.',
      backgroundUrl: '/images/name-plates/spades-100-nameplate.png',
      backgroundStyle: 'background-image: url(/images/name-plates/spades-100-nameplate.png) !important;'
    }
  ];


  constructor() {
    this.nameplates.set(this.defaultCatalog);
  }

  getAll() {
    this.httpClient.get<any[]>(environment.platformUrl + "api/nameplate/list")
      .subscribe({
        next: (res) => {
          if (res && res.length > 0) {
            const mapped = res.map(item => {
              const rawUrl = item.backgroundUrl || item.BackgroundUrl || '';
              const id = item.id || item.Id;
              const matchedCatalog = this.defaultCatalog.find(c => c.id === id);
              return {
                id: id,
                title: matchedCatalog?.title || item.title || item.Description || 'Nameplate',
                description: matchedCatalog?.description || item.description || item.Description || '',
                backgroundUrl: matchedCatalog?.backgroundUrl || rawUrl,
                backgroundStyle: matchedCatalog?.backgroundStyle || `background-image: url(${rawUrl}) !important;`
              };
            });

            const merged = [...this.defaultCatalog];
            mapped.forEach(m => {
              const idx = merged.findIndex(c => c.id === m.id);
              if (idx >= 0) {
                merged[idx] = { ...merged[idx], ...m };
              } else {
                merged.push(m);
              }
            });
            this.nameplates.set(merged);
          }
        },
        error: () => {
          this.nameplates.set(this.defaultCatalog);
        }
      });
  }

  getOwned() {
    return this.httpClient.get<any[]>(environment.platformUrl + "api/nameplate/owned").pipe(
      tap(owned => this.ownedNameplates.set(owned || []))
    );
  }

  update(id: number) {
    return this.httpClient.get(environment.platformUrl + "api/nameplate/update/" + id);
  }
}
